// ─── Lazy Variant Loading (B) ────────────────────────────────────────────────
//
// MASALAH YANG DISELESAIKAN
// Halaman detail produk mengirim SELURUH baris kombinasi varian ke browser.
// Untuk produk preorder besar itu 4.644 baris — padahal picker-nya hanya butuh
// POHON OPSI, yang untuk produk yang sama cuma 64 baris (73× lebih kecil).
// Diukur di PROD (anon, sesudah mig 3600+3620): query detail produk mega-varian
// 124 ms pada saat lapang, 13.529 ms saat CPU instance ter-throttle — sementara
// resolusi SATU kombinasi lewat `option_ids` hanya 14–52 ms.
//
// Dan baris-baris itu memang tak membawa apa-apa: dari 4.644 kombinasi produk
// terbesar, 4.625 ber-harga NULL, 0 ber-stok, dan hanya 19 yang pernah dipesan.
// Se-database: 45.933 dari 49.880 baris varian (92%) tak pernah dipesan sama
// sekali.
//
// BENTUKNYA
// Produk yang melewati ambang TIDAK mengirim daftar kombinasi. Picker dirender
// dari pohon opsi, pembeli memilih per grup, lalu SATU kombinasi diresolusi
// on-demand lewat `option_ids` (identitas sebenarnya — terbukti unik untuk
// seluruh 49.471 baris ber-opsi, sedangkan `name` adalah label turunan yang
// bisa desync: 1 baris di PROD sudah menyimpang dari nama turunannya).
//
// KENAPA HANYA PREORDER — dan ini pembatas KEBENARAN, bukan sekadar kehati-
// hatian: pada ready stock, tombol opsi di-grey-out berdasarkan stok agregat
// lintas kombinasi (`isVariantOptionSelectable` → `hasInStockVariationForOption`).
// Keputusan itu MUSTAHIL diambil tanpa memegang seluruh kombinasi, jadi ready
// stock wajib tetap memuat semuanya. Untungnya itu memang murah: seluruh varian
// ready stock se-database hanya 2.741 baris, dan semua produk mega-varian
// (>1000) bertipe preorder.
//
// SEMUA KOMBINASI TETAP BISA DIPESAN — barisnya tetap ada di DB, hanya tidak
// ikut dikirim. Ini yang membedakannya dari "potong daftar varian", yang akan
// membuat kombinasi di luar potongan mustahil dipesan (kelas bug mig 212).

import type { ProductVariation, VariantGroup } from "@/lib/products";

/**
 * Ambang jumlah kombinasi. Di atas ini, produk preorder berhenti mengirim
 * daftar varian. Dipilih dari sebaran PROD: 1.247 produk (95%) punya <=50
 * kombinasi dan 66 produk lagi <=200 — jadi ambang 200 membuat 99% katalog
 * berperilaku PERSIS seperti sebelumnya, dan hanya 48 produk (yang memegang
 * 71% seluruh baris varian) yang masuk jalur malas.
 */
export const LAZY_VARIANT_THRESHOLD = 200;

/**
 * Batas baris varian yang diminta saat memuat detail produk. Sengaja
 * ambang + 1: satu baris lebih dari ambang sudah cukup membuktikan "produk ini
 * melewati ambang" tanpa perlu query COUNT terpisah, dan produk raksasa tak
 * pernah mentransfer lebih dari 201 baris walau punya 4.644.
 */
export const VARIANT_FETCH_CAP = LAZY_VARIANT_THRESHOLD + 1;

/**
 * Apakah produk ini dimuat dengan mode malas?
 *
 * `fetchedCount >= VARIANT_FETCH_CAP` berarti embed menyentuh batas → masih ada
 * sisa yang tak terbawa. Daftar yang tak lengkap itu BERBAHAYA bila dipakai apa
 * adanya (picker akan mengunci opsi yang sebenarnya ada — persis kelas bug
 * "kombinasi tak bisa dipesan"), jadi begitu batas tersentuh daftarnya DIBUANG
 * dan resolusi dialihkan ke on-demand.
 */
export function shouldLazyLoadVariants(input: {
  productType?: "preorder" | "ready_stock";
  fetchedCount: number;
  cap?: number;
}): boolean {
  const cap = input.cap ?? VARIANT_FETCH_CAP;
  // Ready stock TIDAK PERNAH malas — grey-out opsi butuh stok seluruh kombinasi.
  if (input.productType !== "preorder") return false;
  return input.fetchedCount >= cap;
}

/**
 * MATERIALISASI MALAS (A): apakah penyimpanan produk ini boleh MELEWATKAN
 * kombinasi yang belum membawa nilai apa pun?
 *
 * Ambangnya SENGAJA sama dengan mode malas baca (`shouldLazyLoadVariants`):
 * produk yang variannya tak dikirim ke browser adalah produk yang sama yang
 * kombinasinya tak perlu dimaterialisasi. Kalau dua ambang ini berbeda, akan
 * ada produk yang barisnya tak dibuat tapi tetap dikirim penuh (atau
 * sebaliknya) — dua perilaku yang saling menjelaskan jadi tak sinkron.
 *
 * PREORDER saja. Pada ready stock, baris varian memegang STOK: tanpa baris,
 * barang tak bisa dijual sama sekali, jadi materialisasi malas di sana bukan
 * penghematan melainkan kerusakan.
 */
export function shouldSkipInertVariantRows(input: {
  productType?: "preorder" | "ready_stock";
  comboCount: number;
}): boolean {
  if (input.productType !== "preorder") return false;
  return input.comboCount > LAZY_VARIANT_THRESHOLD;
}

/**
 * Keputusan SISI TULIS: boleh melewatkan INSERT kombinasi inert saat Simpan?
 *
 * Sama dengan `shouldSkipInertVariantRows`, KECUALI produk PO terbatas
 * (`po_limited`, mig 3970). Di sana daftar kombinasinya TERTUTUP:
 * `resolve_or_create_preorder_variant` (mig 5830) menolak melahirkan baris saat
 * dipesan — "Kombinasi … tidak dijual pada PO terbatas ini". Materialisasi
 * malas bersandar pada kelahiran on-demand itu, jadi pada PO terbatas kombinasi
 * yang tak ditulis sekarang TAK PERNAH bisa dipesan. Dan `isInertVariantRow`
 * tidak menghitung `po_quota`, sehingga baris yang HANYA berisi kuota — keadaan
 * normal produk PO baru yang harganya menyusul — ikut tersaring.
 *
 * Sengaja fungsi TERPISAH, bukan parameter baru `shouldSkipInertVariantRows`:
 * fungsi itu juga dipakai sisi BACA storefront (`public-data.ts`) dengan janji
 * "ambang tulis = ambang baca". Sisi baca tetap benar untuk PO terbatas —
 * resolusi on-demand menemukan baris yang kini memang ada.
 */
export function skipInertInsertsOnSave(input: {
  productType?: "preorder" | "ready_stock";
  comboCount: number;
  poLimited: boolean;
}): boolean {
  if (input.poLimited) return false;
  return shouldSkipInertVariantRows(input);
}

/**
 * Keputusan SISI MUAT form: kombinasi kartesius yang TIDAK PUNYA BARIS di DB —
 * tampil DICENTANG (dijual) atau tidak di papan "Kombinasi yang Dijual"?
 *
 * Baris yang hilang hanya berarti "hidup, lahir saat dipesan" pada produk yang
 * sisi tulisnya memang boleh MELEWATKAN kombinasi inert — dan itu persis
 * `skipInertInsertsOnSave`. Di sana `resolve_or_create_preorder_variant`
 * melahirkannya saat dipesan, jadi papan wajib mencentangnya.
 *
 * Di produk LAIN (ready stock, preorder ≤ ambang, PO terbatas) sisi tulis tak
 * pernah melewatkan apa pun, jadi baris yang hilang bukan kombinasi malas —
 * dan storefront memang tak menjualnya: ready stock tanpa baris tak punya
 * barang, picker preorder non-malas tak menemukan kombinasinya, PO terbatas
 * ditolak resolver. Asal-usul terbanyaknya: produk yang DIBUAT sebelum
 * 2026-10-15 dengan kombinasi tak dicentang — form saat itu tidak membuat
 * barisnya sama sekali. Memuatnya sebagai DICENTANG membuat centang admin
 * "kembali" tiap produk dibuka, dan Simpan berikutnya menjualnya (laporan
 * pemilik 2026-10-15; PROD: 234 produk, 6.896 kombinasi).
 *
 * Sengaja fungsi bernama sendiri walau isinya sama: yang dijaga adalah
 * KESETARAANNYA — "boleh tak ditulis" dan "tak tertulis berarti hidup" harus
 * satu keputusan. Mengubah salah satu tanpa yang lain akan menghidupkan lagi
 * kombinasi yang dimatikan admin (atau mematikan kombinasi malas yang sah).
 */
export function absentComboSellable(input: {
  productType?: "preorder" | "ready_stock";
  comboCount: number;
  poLimited: boolean;
}): boolean {
  return skipInertInsertsOnSave(input);
}

/**
 * Jumlah kombinasi kartesius sebuah produk, DITURUNKAN DARI POHON OPSI
 * (hasil kali jumlah opsi tiap grup) — bukan dari jumlah baris varian yang
 * ada di DB.
 *
 * KENAPA HARUS DARI POHON, bukan dari baris: sejak materialisasi malas (A),
 * "berapa baris yang ada" TIDAK LAGI menjawab "seberapa besar produk ini".
 * Produk 800 kombinasi yang seluruh kombinasinya masih inert punya NOL baris.
 * Sisi baca yang menyimpulkan besar-kecil dari jumlah baris terambil akan
 * membacanya sebagai "produk tanpa varian" dan mematikan tombol pesan —
 * bug yang pernah hidup di PROD (ZURIAH by MOSLEM DAILY, 16×5×10 = 800
 * kombinasi, 0 baris, tak bisa dipesan sama sekali).
 *
 * Pohon opsi selalu ikut terkirim di query detail produk (`variant_groups` +
 * `variant_options`) dan ukurannya kecil, jadi angka ini gratis.
 *
 * Grup tanpa opsi menghasilkan 0 — kombinasi memang tak terbentuk. Produk
 * tanpa grup sama sekali (varian legacy pra-sistem grup) juga 0, sehingga ia
 * TIDAK ikut jalur malas dan tetap memakai daftar barisnya seperti dulu.
 */
export function deriveComboCount(optionCounts: readonly number[]): number {
  if (optionCounts.length === 0) return 0;
  return optionCounts.reduce((total, count) => total * count, 1);
}

/**
 * Picker varian harus memakai MODE MALAS — memilih per OPTION ID lalu
 * meresolusi kombinasinya saat ditambahkan — alih-alih memilih dari daftar
 * baris yang ada?
 *
 * DUA syarat. Pertama, kartesiusnya melewati `LAZY_VARIANT_THRESHOLD`: hanya
 * di sana kombinasi TANPA baris berarti "hidup, lahir saat dipesan"
 * (`absentComboSellable`). Pada produk ≤ ambang, baris yang hilang adalah
 * kombinasi yang TIDAK dijual — dan sumbernya masih ada walau form sudah
 * menulis kartesius penuh (#1353): impor Excel hanya menulis baris yang
 * didaftar. Tanpa syarat ini Edit Pesanan pembeli dan Tambah Produk keranjang
 * admin melahirkan baris AKTIF untuk kombinasi yang tak pernah ditawarkan —
 * pesanan masuk ke barang yang tak dijual, lalu kombinasinya terjual ke semua
 * orang (cari-bug 2026-10-15; mekanisme insiden mig 7330). Produk ≤ ambang
 * pun selalu termuat utuh di layar pemanggil (≥ 1.000 baris), jadi picker
 * lama melihat setiap kombinasi yang memang ada.
 *
 * Kedua, SELISIH: layar yang memanggil ini memuat SELURUH baris (terpaginasi,
 * atau `allVariants`), jadi ia tahu persis berapa kombinasi yang ada di pohon
 * opsi DAN berapa yang sudah punya baris. Selisih nol → picker lama dipakai
 * PERSIS seperti sebelumnya, sehingga produk yang kombinasinya memang lengkap
 * (mayoritas katalog) tidak tersentuh. PO terbatas di atas ambang sengaja
 * tetap ikut aturan selisih (muatan Edit Pesanan dibatasi 1.000 baris); di
 * sana `resolve_or_create_preorder_variant` sendiri yang menolak melahirkan
 * baris.
 *
 * PREORDER saja, dan itu pembatas KEBENARAN bukan kehati-hatian:
 * `resolve_or_create_preorder_variant` MENOLAK produk non-preorder
 * ("Materialisasi varian hanya untuk produk pre-order."), dan pada ready stock
 * baris varianlah yang memegang STOK — kombinasi tanpa baris memang tak punya
 * barang untuk dijual, jadi membiarkannya dipilih adalah menjual udara.
 *
 * Grup tanpa opsi membuat kartesius 0 (`deriveComboCount`) → tidak malas:
 * struktur variasinya belum selesai diisi admin, tak ada yang bisa diresolusi.
 *
 * Tinggal di modul ini, bukan di `adminCart.ts`, karena pemanggilnya ada di
 * KEDUA sisi: keranjang/PO admin dan Edit Pesanan pembeli. `EditOrderModal`
 * sengaja dimuat `next/dynamic` agar bundel pembeli tidak menyeret klien
 * Supabase + housekeeping yang ikut terbawa `adminCart.ts` (PF-19).
 */
export function variantPickerIsLazy(input: {
  productType?: "preorder" | "ready_stock";
  variantGroups: readonly VariantGroup[];
  materializedCount: number;
}): boolean {
  if (input.productType !== "preorder") return false;
  if (input.variantGroups.length === 0) return false;
  const total = deriveComboCount(input.variantGroups.map((g) => g.options.length));
  if (total <= LAZY_VARIANT_THRESHOLD) return false;
  return total > input.materializedCount;
}

/**
 * Baris/varian yang SUDAH bermateri untuk kombinasi ini — `undefined` bila
 * belum ada (pemanggil lalu meminta RPC membuatnya).
 *
 * Dicocokkan lewat `option_ids` yang INDEPENDEN URUTAN (`comboBlockKey`), bukan
 * lewat nama: `option_ids` adalah identitas sebenarnya sebuah kombinasi,
 * sedangkan `name` label turunan yang bisa menyimpang (di PROD ada 1 baris yang
 * begitu). Urutannya pun tak bisa diandalkan — `option_ids` di DB mengikuti
 * urutan grup saat baris DITULIS, sementara pilihan di layar dinormalkan ulang
 * `selectOptionInGroup` setiap kali opsi diketuk.
 */
export function findVariationByOptionIds<
  T extends { optionIds?: readonly string[] }
>(rows: readonly T[], optionIds: readonly string[]): T | undefined {
  if (optionIds.length === 0) return undefined;
  const key = comboBlockKey(optionIds);
  return rows.find(
    (r) => (r.optionIds?.length ?? 0) > 0 && comboBlockKey(r.optionIds!) === key
  );
}

/**
 * Boleh halaman produk bertanya HARGA WARISAN SLOT
 * (`preorder_inherited_pricing`, mig 5510) untuk kombinasi yang sedang
 * dipilih? Bila barisnya BELUM ADA (`resolvePublicVariant` → `null`), ATAU —
 * sejak 2026-10-20 — barisnya ADA tapi belum berharga. Baris yang sudah
 * berharga adalah kebenaran tunggalnya; warisan tak pernah menimpanya.
 *
 * Untuk baris yang belum ada, angka warisan sah karena ia yang AKAN ditulis
 * `resolve_or_create_preorder_variant` saat baris itu dibuat.
 *
 * Untuk baris yang ADA tapi belum berharga, syaratnya KESETARAAN DUA SISI.
 * Bug live galleryhasanah 2026-09-25: 18 kombinasi baru (Broken White/Black
 * × Mom Pants Only) dicentang tanpa harga; layar (mode malas) memajang
 * Rp 375.000 — warisan dari Pants warna lama yang sudah DIMATIKAN — sementara
 * resolver checkout membaca baris itu TANPA warisan dan mengunci `unit_price`
 * NULL. Layar ≠ nota, dan gerbang ini lalu menolak warisan untuk baris yang
 * ada. Sejak celah "harga seragam" (Mukena 17 Sep, mig 7680) resolver checkout
 * (`buildCheckoutLineResolver`) memanggil RPC warisan yang SAMA untuk baris
 * seperti itu — pada produk ber-sumbu harga — sehingga angka yang dipajang
 * dan angka yang dikunci lahir dari satu sumber; gerbangnya dibuka lagi.
 * Pembedanya bukan ada/tidaknya baris, melainkan ada/tidaknya HARGA.
 */
export function shouldUseInheritedPricing(
  variant: { price_normal?: number } | null | undefined
): boolean {
  return variant == null || variant.price_normal === undefined;
}

/** Pilihan bawaan: opsi PERTAMA tiap grup (grup & opsi sudah terurut mapper). */
export function defaultOptionSelection(groups: VariantGroup[]): string[] {
  return groups
    .map((group) => group.options[0]?.id)
    .filter((id): id is string => Boolean(id));
}

/**
 * Terapkan klik pada satu opsi: pilihan grup LAIN dipertahankan, pilihan di
 * grup opsi ini diganti. Hasilnya selalu urut mengikuti urutan grup supaya
 * dua pilihan yang sama menghasilkan array yang sama (penting: array ini jadi
 * kunci cache resolusi dan argumen query).
 */
export function selectOptionInGroup(
  groups: VariantGroup[],
  current: string[],
  optionId: string
): string[] {
  const owner = groups.find((g) => g.options.some((o) => o.id === optionId));
  if (!owner) return current;
  return groups
    .map((group) => {
      if (group.id === owner.id) return optionId;
      return group.options.find((o) => current.includes(o.id))?.id;
    })
    .filter((id): id is string => Boolean(id));
}

/** Opsi yang sedang aktif di sebuah grup (untuk highlight tombol). */
export function selectedOptionInGroup(
  group: VariantGroup,
  selected: string[]
): string | undefined {
  return group.options.find((o) => selected.includes(o.id))?.id;
}

/** Sudah memilih tepat satu opsi di SETIAP grup? */
export function isSelectionComplete(
  groups: VariantGroup[],
  selected: string[]
): boolean {
  if (groups.length === 0) return false;
  return groups.every((group) =>
    group.options.some((o) => selected.includes(o.id))
  );
}

/**
 * Label kombinasi dari opsi terpilih — CERMIN cara `product_variants.name`
 * dibentuk ("Merah / L", urut grup). Dipakai HANYA untuk tampilan sementara
 * sebelum baris aslinya ter-resolusi; identitas untuk query tetap `option_ids`
 * (di PROD ada 1 baris yang namanya sudah menyimpang dari turunannya, jadi
 * nama TIDAK boleh dipakai sebagai kunci pencarian).
 */
export function deriveVariantLabel(
  groups: VariantGroup[],
  selected: string[]
): string {
  return groups
    .map((group) => group.options.find((o) => selected.includes(o.id))?.name)
    .filter((name): name is string => Boolean(name))
    .join(" / ");
}

/**
 * Varian yang DINILAI harganya untuk pilihan di layar (bughunt D16-13).
 *
 * Mode malas: kombinasi lengkap yang barisnya belum ada (`variation` undefined)
 * dulu dikirim ke `resolveSelectionPrice` sebagai "tak ada pilihan", sehingga
 * pengecekan sebaran harga dilewati dan lapis `products.price_normal` (MIN
 * seluruh varian) dipajang sebagai harga pasti — lalu masuk keranjang —
 * sementara checkout menilai baris yang dibuat dari kombinasi itu sebagai
 * varian tanpa harga sendiri dan mengunci NULL (harga menyusul). Di sini
 * kombinasi itu diwakili varian SINTETIS tanpa harga (`price_normal`
 * undefined), persis bentuk baris yang akan dibuat `resolveOrCreatePreorderVariant`,
 * jadi layar dan checkout menjawab pertanyaan yang sama. Pilihan belum lengkap
 * / bukan mode malas = apa adanya.
 */
export function pricingSelection(input: {
  variation: ProductVariation | undefined;
  variantsLazy: boolean | undefined;
  groups: VariantGroup[];
  selectedOptionIds: readonly string[];
}): ProductVariation | undefined {
  if (input.variation) return input.variation;
  const ids = [...input.selectedOptionIds];
  if (!input.variantsLazy || !isSelectionComplete(input.groups, ids)) return undefined;
  return { id: "", name: deriveVariantLabel(input.groups, ids), sku: "", optionIds: ids };
}

/** Kunci cache stabil untuk sebuah kombinasi (urutan sudah dinormalkan). */
export function optionSelectionKey(selected: string[]): string {
  return selected.join("|");
}

// ─── Kombinasi yang DIMATIKAN admin (product_variants.is_active = false) ─────
//
// MASALAH YANG DISELESAIKAN (live di PROD, natasyashop — "Abinaya famset by
// medina", 4×11×10 = 440 kombinasi, 108 di antaranya nonaktif): admin
// mematikan kombinasi tertentu (di produk itu: Scraft mom / Pashmina mom /
// Pashmina girl hanya dijual "All size"), tapi pembeli tetap bisa mengkliknya,
// menambahkannya ke keranjang, dan baru ditolak di checkout dengan
// "Variasi … sudah tidak tersedia (produk baru saja diperbarui admin)".
//
// SEBABNYA pembeli MUSTAHIL membedakan dua keadaan yang tampak sama: policy
// RLS "Public can read product variants" menuntut `is_active = true`, jadi
// baris yang dimatikan TIDAK TERLIHAT sama sekali oleh pembeli — persis
// seperti kombinasi yang barisnya memang belum bermateri. Yang pertama haram
// dipesan, yang kedua justru alur normal mode malas (barisnya dibuat saat
// ditambahkan ke keranjang). `resolvePublicVariant` memulangkan `null` untuk
// KEDUANYA.
//
// Karena itu daftar kombinasi mati dikirim TERPISAH lewat RPC — hanya
// `option_ids`-nya, bukan nama atau harga — dan helper di bawah
// menerjemahkannya jadi keputusan enable/disable tombol opsi. Gerbang yang
// MENGIKAT tetap di DB (`resolve_or_create_preorder_variant` menolak baris
// nonaktif); helper ini membuat penolakan itu terlihat SEBELUM pembeli
// mengetuk, bukan sesudah.
//
// DAFTARNYA WAJIB UTUH — pelajaran mig 6300 (galleryhasanah "Sarimbit Djiwana
// Series", 1.079 kombinasi mati). RPC pertamanya (`preorder_blocked_combos`,
// mig 5830, dipensiunkan mig 6310) memulangkan satu BARIS per kombinasi, dan PostgREST memotong
// respons berbentuk baris di `max-rows` 1.000: 79 kombinasi mati tampil
// dijual. Daftar terpotong BUKAN "kurang mencoret tapi aman" — kombinasi yang
// tak ada di daftar dianggap hidup, sehingga `resolveSelectionForOption`
// menjadikannya TUJUAN pergeseran (298 ketukan dari kombinasi yang dijual
// mendarat di kombinasi mati). Karena itu sumbernya kini
// `preorder_blocked_combo_list`: SATU nilai jsonb, tak punya baris untuk
// dipotong. Jangan kembali ke RPC berbentuk baris tanpa paginasi.
//
// Arah kegagalan: daftar kosong / gagal dimuat = tak ada yang dikunci, persis
// perilaku sebelum mig 5830, dan gerbang DB tetap menahan.

/** Daftar `option_ids` kombinasi yang dimatikan admin. */
export type BlockedCombos = readonly (readonly string[])[];

/**
 * Himpunan kunci kombinasi yang tak bisa dipilih. Pemilih HANYA memakai
 * `.size` ("ada yang dikunci?") dan `.has` — `ReadonlySet` memenuhinya, begitu
 * pula himpunan komplemen daftar-izin produk tertutup (`pickerBlockedSet`,
 * comboListMode.ts) yang tak pernah membangun komplemennya.
 */
export type BlockedComboKeys = { readonly size: number; has(key: string): boolean };

/**
 * Baca hasil RPC `preorder_blocked_combo_list` (mig 6300): SATU nilai jsonb
 * berbentuk array `option_ids`, mis. `[["w1","s1","y1"], …]`.
 *
 * Bentuk yang tak dikenal (bukan array, elemen bukan array, id bukan string)
 * dibuang, bukan dilempar — arah gagal yang sama dengan daftar yang gagal
 * dimuat: yang tak terbaca tidak dikunci, dan gerbang DB tetap menahan.
 * Id kosong disaring per kombinasi; kombinasi yang jadi kosong dibuang supaya
 * tak lahir kunci hampa (cermin `buildBlockedComboSet`).
 */
export function parseBlockedComboList(data: unknown): string[][] {
  if (!Array.isArray(data)) return [];
  const out: string[][] = [];
  for (const combo of data) {
    if (!Array.isArray(combo)) continue;
    const ids = combo.filter(
      (id): id is string => typeof id === "string" && id.length > 0
    );
    if (ids.length > 0) out.push(ids);
  }
  return out;
}

/**
 * Kunci kombinasi yang INDEPENDEN URUTAN — `option_ids` di DB mengikuti urutan
 * grup saat baris ditulis, sedangkan pilihan di layar dinormalkan
 * `selectOptionInGroup`. Dua urutan berbeda untuk kombinasi yang sama tak
 * boleh menghasilkan dua kunci (idiom `comboMatchKey` di variantPricing.ts).
 */
export function comboBlockKey(optionIds: readonly string[]): string {
  return [...optionIds].sort().join("|");
}

/** Himpunan kunci siap-cari dari daftar mentah RPC. */
export function buildBlockedComboSet(combos: BlockedCombos): ReadonlySet<string> {
  const out = new Set<string>();
  for (const combo of combos) {
    if (!combo || combo.length === 0) continue;
    out.add(comboBlockKey(combo));
  }
  return out;
}

/** Kombinasi LENGKAP ini dimatikan admin? */
export function isComboBlocked(
  blocked: BlockedComboKeys,
  optionIds: readonly string[]
): boolean {
  if (blocked.size === 0 || optionIds.length === 0) return false;
  return blocked.has(comboBlockKey(optionIds));
}

/**
 * Pilihan yang SEDANG terlihat di picker mode malas adalah kombinasi yang
 * dimatikan admin — tombol Tambah wajib mati di sini. Pilihan yang belum
 * lengkap bukan kombinasi apa pun, jadi tak pernah dianggap mati.
 */
export function isSelectionBlocked(
  groups: VariantGroup[],
  selected: readonly string[],
  blocked: BlockedComboKeys
): boolean {
  if (blocked.size === 0) return false;
  if (!isSelectionComplete(groups, [...selected])) return false;
  return isComboBlocked(blocked, selected);
}

/**
 * Daftar kombinasi mati dari baris varian yang SUDAH dipegang pemanggil.
 *
 * Layar admin (keranjang admin, Kelola PO) membaca SELURUH baris lewat sesi
 * admin — termasuk yang dimatikan — lalu membuangnya dari daftar pilihan
 * (`fetchClosedPOEditContext`). Tanpa daftar ini, baris yang dibuang itu
 * tampak persis seperti kombinasi yang belum bermateri: picker mode malas
 * membiarkannya diketuk, dan RPC materialisasi baru menolaknya ("Variasi …
 * sedang tidak dijual") — 8 penolakan di keranjang admin galleryhasanah
 * 2026-09-25. Karena barisnya sudah di tangan, daftarnya diturunkan di sini,
 * tanpa RPC `preorder_blocked_combo_list` (yang ada untuk pembeli: RLS
 * menyembunyikan baris mati dari mereka).
 */
export function blockedCombosFromRows(
  rows: readonly {
    isActive: boolean;
    optionIds?: readonly (string | null | undefined)[] | null;
  }[]
): string[][] {
  const out: string[][] = [];
  for (const row of rows) {
    if (row.isActive) continue;
    const ids = (row.optionIds ?? []).filter(
      (id): id is string => typeof id === "string" && id.length > 0
    );
    if (ids.length > 0) out.push(ids);
  }
  return out;
}

/**
 * Kombinasi yang terbentuk bila opsi ini diketuk SEKARANG dimatikan admin?
 * SADAR-SILANG: yang dinilai bukan opsinya sendirian, melainkan kombinasi
 * LENGKAP bersama pilihan grup lain yang sedang terlihat.
 *
 * ⚠️ INI BUKAN ALASAN MENGUNCI TOMBOL — pelajaran mahal, dibayar di PROD.
 * Sampai 2026-09-14 jawaban `true` di sini langsung menonaktifkan tombolnya,
 * dan itu MENJEBAK pembeli pada produk yang butuh DUA grup berubah sekaligus.
 * natasyashop "Navara famset": halaman terbuka di `Burgundi night / Koko Basic
 * pendek / XS`, sementara Pashmina hanya dijual pada ukuran Mom dan Kids. Tiap
 * langkah satu-opsi dari sana mendarat di kombinasi mati —
 *
 *   klik Pashmina  → Burgundi / Pashmina / XS            (mati)
 *   klik Mom       → Burgundi / Koko Basic pendek / Mom  (mati)
 *   klik Kids      → Burgundi / Koko Basic pendek / Kids (mati)
 *
 * — sehingga `Burgundi night / Pashmina / Mom` yang HIDUP tak punya satu pun
 * jalan masuk, dan pemilik melaporkan "varian pashmina tidak bisa dipilih".
 *
 * Jadi jawaban `true` di sini kini berarti tombolnya REDUP dan mengetuknya
 * MENGGESER grup lain ke kombinasi hidup terdekat (`resolveSelectionForOption`).
 * Yang boleh mengunci hanya `isOptionFullyBlocked` — opsi yang mati di SELURUH
 * kombinasi, tempat tak ada yang bisa dijebak karena memang tak ada tujuan.
 *
 * Pilihan yang belum lengkap → `false`: kombinasi yang mau dinilai belum
 * terbentuk, dan menebak berarti menandai opsi yang sebenarnya sah.
 */
export function isOptionBlocked(
  groups: VariantGroup[],
  selected: string[],
  optionId: string,
  blocked: BlockedComboKeys
): boolean {
  if (blocked.size === 0) return false;
  const candidate = selectOptionInGroup(groups, selected, optionId);
  if (!isSelectionComplete(groups, candidate)) return false;
  return isComboBlocked(blocked, candidate);
}

/** Batas penelusuran kartesius — produk terbesar di PROD 4.644 kombinasi. */
const SELECTION_SCAN_LIMIT = 20000;

/**
 * Jalankan `visit` untuk setiap kombinasi kartesius yang memuat `optionId` —
 * yaitu hasil kali opsi SELURUH GRUP LAIN, bukan seluruh kartesius produk.
 * Untuk "Navara famset" itu 76 kombinasi, bukan 1.216.
 *
 * `false` bila daftarnya terlalu besar untuk ditelusuri (pemanggil lalu memilih
 * jawaban yang tidak mengunci apa pun); `true` bila penelusurannya tuntas.
 * `visit` boleh menghentikan penelusuran lebih awal dengan memulangkan `false`.
 */
function forEachComboWithOption(
  groups: VariantGroup[],
  optionId: string,
  visit: (combo: string[]) => boolean
): boolean {
  const owner = groups.find((g) => g.options.some((o) => o.id === optionId));
  if (!owner) return false;
  const others = groups.filter((g) => g.id !== owner.id);
  if (others.some((g) => g.options.length === 0)) return false;

  const counts = others.map((g) => g.options.length);
  const total = counts.reduce((a, b) => a * b, 1);
  if (total > SELECTION_SCAN_LIMIT) return false;

  const index = counts.map(() => 0);
  for (let scanned = 0; scanned < total; scanned += 1) {
    // Urutan mengikuti `groups` supaya hasilnya bisa langsung dipakai sebagai
    // pilihan (cermin `selectOptionInGroup`).
    let cursor = 0;
    const combo = groups.map((g) => {
      if (g.id === owner.id) return optionId;
      return g.options[index[cursor++]].id;
    });
    if (!visit(combo)) return true;

    let i = counts.length - 1;
    while (i >= 0) {
      index[i] += 1;
      if (index[i] < counts[i]) break;
      index[i] = 0;
      i -= 1;
    }
    if (i < 0) break;
  }
  return true;
}

/**
 * Opsi ini mati di SELURUH kombinasi — SATU-SATUNYA alasan yang sah untuk
 * mengunci tombolnya. Bedanya dengan `isOptionBlocked` dijelaskan panjang di
 * sana: mengunci berdasarkan kombinasi yang sedang terlihat pernah membuat
 * varian yang masih dijual mustahil dicapai.
 *
 * Produk yang terlalu besar untuk ditelusuri → `false` (jangan mengunci).
 * Gerbang DB tetap menahan yang benar-benar tak boleh dipesan.
 */
export function isOptionFullyBlocked(
  groups: VariantGroup[],
  optionId: string,
  blocked: BlockedComboKeys
): boolean {
  if (blocked.size === 0) return false;
  let semuaMati = true;
  const tuntas = forEachComboWithOption(groups, optionId, (combo) => {
    if (isComboBlocked(blocked, combo)) return true;
    semuaMati = false;
    return false; // satu yang hidup sudah cukup — berhenti.
  });
  return tuntas && semuaMati;
}

/**
 * Pilihan yang harus BERLAKU setelah pembeli mengetuk `optionId`.
 *
 * Biasanya cukup `selectOptionInGroup` (ganti opsi di grupnya, pertahankan
 * grup lain). Tapi bila kombinasi itu dimatikan admin, mempertahankan grup
 * lain berarti mengantar pembeli ke pintu tertutup — jadi grup lain DIGESER
 * ke kombinasi hidup TERDEKAT yang tetap memuat opsi yang ia ketuk.
 *
 * "Terdekat" = paling sedikit grup lain yang ikut berubah; seri diputus oleh
 * urutan kartesius (opsi pertama tiap grup lebih dulu), supaya dua pembeli
 * dengan pilihan sama selalu mendarat di kombinasi yang sama.
 *
 * Aturan yang sama dengan yang sudah dipakai mode NON-malas di
 * `VariantGroupSelector` sejak #1025 (di sana daftar variannya ada, jadi
 * pergeserannya jatuh dari pencarian varian terbaik). Pergeserannya wajib
 * dilaporkan ke pembeli — `describeVariantShift` di `products.ts`.
 */
export function resolveSelectionForOption(
  groups: VariantGroup[],
  current: string[],
  optionId: string,
  blocked: BlockedComboKeys
): string[] {
  const direct = selectOptionInGroup(groups, current, optionId);
  if (blocked.size === 0) return direct;
  if (!isSelectionComplete(groups, direct)) return direct;
  if (!isComboBlocked(blocked, direct)) return direct;

  let best: string[] | null = null;
  let bestJarak = Number.POSITIVE_INFINITY;
  forEachComboWithOption(groups, optionId, (combo) => {
    if (isComboBlocked(blocked, combo)) return true;
    const jarak = combo.reduce(
      (n, id) => (direct.includes(id) ? n : n + 1),
      0
    );
    if (jarak < bestJarak) {
      bestJarak = jarak;
      best = combo;
    }
    return true;
  });
  // Tak ada kombinasi hidup (atau produknya terlalu besar untuk ditelusuri) →
  // biarkan pilihan apa adanya; tombol pesan yang menahan, bukan tombol opsi.
  return best ?? direct;
}

/**
 * Pilihan bawaan yang BENAR-BENAR bisa dipesan: opsi pertama tiap grup, tapi
 * bila kombinasi itu sendiri dimatikan admin, geser ke kombinasi hidup
 * pertama dalam urutan kartesius.
 *
 * Tanpa ini halaman produk bisa terbuka dengan tombol pesan yang sudah mati
 * dan pembeli harus menebak sendiri kombinasi mana yang dijual.
 *
 * Semua kombinasi mati (atau produk terlalu besar untuk ditelusuri) → kembali
 * ke pilihan bawaan: layar tetap menampilkan sesuatu, dan tombol pesannya yang
 * menahan.
 */
export function firstSelectableSelection(
  groups: VariantGroup[],
  blocked: BlockedComboKeys
): string[] {
  const fallback = defaultOptionSelection(groups);
  if (blocked.size === 0 || groups.length === 0) return fallback;
  if (!isSelectionComplete(groups, fallback)) return fallback;
  if (!isComboBlocked(blocked, fallback)) return fallback;

  const counts = groups.map((g) => g.options.length);
  if (counts.some((c) => c === 0)) return fallback;

  // Odometer: menaikkan indeks grup TERAKHIR lebih dulu, persis urutan
  // kartesius yang dipakai sisi tulis (`planVariantSave`). Sengaja tidak
  // membangun seluruh daftar kombinasi — produk besar punya ribuan.
  const index = counts.map(() => 0);
  for (let scanned = 0; scanned < SELECTION_SCAN_LIMIT; scanned += 1) {
    let i = counts.length - 1;
    while (i >= 0) {
      index[i] += 1;
      if (index[i] < counts[i]) break;
      index[i] = 0;
      i -= 1;
    }
    if (i < 0) break; // kembali ke awal = seluruh kartesius sudah ditelusuri
    const candidate = groups.map((g, gi) => g.options[index[gi]].id);
    if (!isComboBlocked(blocked, candidate)) return candidate;
  }
  return fallback;
}

// ─── Kegagalan materialisasi saat menambah ke keranjang ─────────────────────
//
// RPC `resolve_or_create_preorder_variant` (mig 3630) MENULIS baris, jadi
// EXECUTE-nya sengaja hanya untuk sesi login. Kegagalannya karena itu punya
// dua kelas yang menuntut dua jawaban berbeda kepada pembeli:
//   • LOGIN_REQUIRED — sesinya anon / kedaluwarsa: yang dibutuhkan adalah
//     masuk, bukan mencoba lagi.
//   • UNAVAILABLE   — jaringan, timeout, atau DB menolak (kombinasi tak sah,
//     PO berkuota, produk nonaktif): mencoba lagi / hubungi admin.
//
// Bug live 2026-09-02 (ZURIAH by MOSLEM DAILY, 800 kombinasi, 0 baris):
// SEMUA kegagalan ini dulu dipetakan ke dialog "Ada keranjang produk lain" —
// pesan mode keranjang satu-produk (kini dihapus) — pada keranjang yang kosong. Pembeli
// tak diberi tahu apa yang sebenarnya perlu ia lakukan.

export type PreorderVariantFailure =
  | "LOGIN_REQUIRED"
  | "REJECTED"
  | "UNAVAILABLE";

export type PreorderVariantResolution =
  | { ok: true; id: string }
  | {
      ok: false;
      reason: PreorderVariantFailure;
      /**
       * Pesan RPC apa adanya — hanya bermakna untuk REJECTED: `raise exception`
       * di RPC memang ditulis untuk dibaca pembeli ("Kombinasi … tidak dijual
       * pada PO terbatas ini."). Untuk alasan lain pesannya teknis dan tak
       * ditampilkan.
       */
      message?: string;
    };

/** Potret error RPC yang relevan (PostgREST `code` + status HTTP + pesan). */
export type PreorderVariantRpcError = {
  code?: string | null;
  status?: number | null;
  message?: string | null;
};

/**
 * Petakan kegagalan RPC ke alasan yang bisa dijelaskan ke pembeli.
 *
 *   • LOGIN_REQUIRED — PostgREST `42501` (permission denied — sesi anon
 *     menembak fungsi yang EXECUTE-nya dicabut dari anon), `PGRST301` (JWT
 *     kedaluwarsa/tak sah), atau HTTP 401.
 *   • REJECTED — `P0001` (`raise exception` di badan RPC): penolakan BISNIS
 *     yang pesannya memang ditulis untuk pembeli dan TIDAK akan berubah bila
 *     dicoba lagi (PO berkuota menutup kombinasi, produk nonaktif, kombinasi
 *     tak sah, toko tak cocok). Menawarkan "Coba lagi" di sini menyesatkan.
 *   • UNAVAILABLE — selain itu, termasuk `null` (timeout/jaringan yang tak
 *     menyisakan error) dan kegagalan DB non-bisnis (mis. 23502 pra-mig 4640):
 *     boleh dicoba lagi. Menyuruh pembeli login untuk kegagalan yang bukan
 *     soal sesi hanya membuatnya login lalu gagal lagi.
 */
export function classifyPreorderVariantFailure(
  error: PreorderVariantRpcError | null | undefined
): PreorderVariantFailure {
  if (!error) return "UNAVAILABLE";
  const code = (error.code ?? "").trim().toUpperCase();
  if (code === "42501" || code === "PGRST301") return "LOGIN_REQUIRED";
  if (error.status === 401) return "LOGIN_REQUIRED";
  const message = error.message ?? "";
  if (/permission denied/i.test(message) || /\bjwt\b/i.test(message)) {
    return "LOGIN_REQUIRED";
  }
  if (code === "P0001") return "REJECTED";
  return "UNAVAILABLE";
}

/**
 * Pesan siap-tampil saat materialisasi kombinasi GAGAL — terjemahan
 * `classifyPreorderVariantFailure` ke kalimat yang bisa dibaca. Hidup di
 * modul ini, bukan di `adminCart.ts`, karena dipakai KEDUA sisi: picker
 * admin dan Edit Pesanan pembeli (lihat catatan `variantPickerIsLazy`):
 *
 *   • REJECTED — penolakan BISNIS dari badan RPC, dan pesannya memang ditulis
 *     untuk dibaca manusia ("Variasi … sedang tidak dijual.", "Kombinasi …
 *     tidak dijual pada PO terbatas ini."). Diteruskan apa adanya; mengarang
 *     ulang di sini hanya menyembunyikan sebab yang sudah tepat.
 *   • LOGIN_REQUIRED — sesi admin habis. Yang dibutuhkan masuk ulang, bukan
 *     mencoba lagi.
 *   • UNAVAILABLE — jaringan/timeout: boleh dicoba lagi.
 */
export function materializeVariantErrorMessage(
  reason: PreorderVariantFailure,
  message?: string,
  audience: "admin" | "pembeli" = "admin"
): string {
  if (reason === "REJECTED" && message?.trim()) return message.trim();
  if (reason === "LOGIN_REQUIRED") {
    return audience === "admin"
      ? "Sesi admin sudah berakhir. Masuk ulang lalu coba lagi."
      : "Sesi login sudah berakhir. Masuk lagi lalu ulangi.";
  }
  return "Gagal menyiapkan varian untuk kombinasi ini. Coba lagi.";
}

// ─── Pilihan yang selamat dari putaran login ────────────────────────────────
//
// Tamu yang diminta MASUK dari dialog di atas kembali lewat `/login?next=…`
// yang me-reload halaman penuh; state React (opsi terpilih + jumlah) tak
// selamat. Pada produk 800 kombinasi itu berarti memilih ulang tiga grup.
// Pilihannya dititipkan di query `next` dan dipulihkan saat mount.

/** Nama parameter query: `v` = option id dipisah koma, `qty` = jumlah. */
export const SELECTION_QUERY_OPTIONS = "v";
export const SELECTION_QUERY_QTY = "qty";

/**
 * Query string (`?v=…&qty=…`) untuk menitipkan pilihan; kosong bila tak ada
 * opsi terpilih. Jumlah di bawah 1 / bukan bilangan bulat dinormalkan ke 1.
 */
export function buildSelectionSearch(optionIds: string[], qty: number): string {
  if (optionIds.length === 0) return "";
  const params = new URLSearchParams();
  params.set(SELECTION_QUERY_OPTIONS, optionIds.join(","));
  const safeQty = Number.isInteger(qty) && qty >= 1 ? qty : 1;
  params.set(SELECTION_QUERY_QTY, String(safeQty));
  return `?${params.toString()}`;
}

/**
 * Pulihkan pilihan dari query string. FAIL-SAFE: opsi hanya dipulangkan bila
 * SETIAP id benar-benar milik pohon opsi produk ini dan tepat SATU per grup
 * (URL bisa diedit tangan / milik produk lain) — selain itu `optionIds` null
 * dan pemanggil memakai pilihan bawaan. Urutannya dinormalkan mengikuti
 * urutan grup (kunci cache & argumen query menuntut itu). `qty` null bila
 * absen atau tak sah.
 */
export function parseSelectionSearch(
  search: string,
  groups: VariantGroup[]
): { optionIds: string[] | null; qty: number | null } {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  } catch {
    return { optionIds: null, qty: null };
  }

  const rawQty = params.get(SELECTION_QUERY_QTY);
  const parsedQty = rawQty === null ? NaN : Number(rawQty);
  const qty = Number.isInteger(parsedQty) && parsedQty >= 1 ? parsedQty : null;

  const raw = params.get(SELECTION_QUERY_OPTIONS);
  if (!raw || groups.length === 0) return { optionIds: null, qty };
  const wanted = new Set(raw.split(",").map((id) => id.trim()).filter(Boolean));
  if (wanted.size !== groups.length) return { optionIds: null, qty };

  const ordered: string[] = [];
  for (const group of groups) {
    const hits = group.options.filter((o) => wanted.has(o.id));
    if (hits.length !== 1) return { optionIds: null, qty };
    ordered.push(hits[0].id);
  }
  return { optionIds: ordered, qty };
}

/**
 * Pilihan dari `searchParams` halaman SERVER (`?v=…&qty=…`), aturan yang SAMA
 * dengan `parseSelectionSearch` (fail-safe, satu opsi per grup). Diurai di
 * server supaya HTML AWAL sudah menampilkan varian yang dituju tautan — tautan
 * item feed Katalog Instagram, `offers.url` JSON-LD, dan jalan pulang login —
 * untuk SEMUA produk (PR-IG-3), bukan varian bawaan yang baru berganti setelah
 * hidrasi. Nilai berulang: yang PERTAMA, perilaku `URLSearchParams.get`.
 */
export function parseSelectionQuery(
  query: Readonly<Record<string, string | string[] | undefined>>,
  groups: VariantGroup[]
): { optionIds: string[] | null; qty: number | null } {
  const params = new URLSearchParams();
  for (const key of [SELECTION_QUERY_OPTIONS, SELECTION_QUERY_QTY]) {
    const raw = query[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === "string") params.set(key, value);
  }
  return parseSelectionSearch(params.toString(), groups);
}
